import { Show } from "solid-js";
import { useAuth } from "~/lib/auth";
import { setPostAuthRedirect } from "~/lib/supabase";
import { Loader } from "~/components/ui/loader";

import {
  Navigate,
  Outlet,
  createFileRoute,
  redirect,
} from "@tanstack/solid-router";

// Stash the protected URL a guard just denied,
// so a successful login returns the user here instead of the default dashboard (consumed at /auth/callback).
// Only the plain unauthenticated bounce to `/` is a login-return target;
// requiresJoin is a distinct post-auth state routed through /join, never re-entered via OAuth.
function rememberDeniedUrl(requiresJoin: boolean, url: string) {
  if (!requiresJoin) setPostAuthRedirect(url);
}

// Runs once when the guard denies access after auth resolves post-mount,
// the cold-load path: a shared protected link opened while logged out.
// This is a plain side effect at mount, not an effect-driven navigate,
// so it doesn't trip the redirect loop the <Navigate> comment below warns about.
function DeniedRedirect(props: { requiresJoin: boolean }) {
  rememberDeniedUrl(
    props.requiresJoin,
    window.location.pathname + window.location.search,
  );
  return <Navigate to={props.requiresJoin ? "/join" : "/"} replace />;
}

function AuthedLayout() {
  const { state } = useAuth();

  // Reactive guard: redirect when auth resolves to unauthenticated.
  // beforeLoad only runs on navigation events, not reactively after mount.
  //
  // ⚠️ DO NOT replace this declarative <Navigate> with a programmatic
  // createEffect(() => navigate(...)) fallback. Doing so reproducibly triggers a
  // browser OOM / dev-server freeze for logged-in users on `/` and `/dashboard`
  // (suspected redirect/render loop interacting with the auth signal). The
  // declarative form is the known-good shape - leave it alone.
  return (
    <Show when={!state.loading} fallback={<Loader />}>
      <Show
        when={state.user}
        fallback={<DeniedRedirect requiresJoin={state.requiresJoin} />}
      >
        <Outlet />
      </Show>
    </Show>
  );
}

export const Route = createFileRoute("/_authed")({
  beforeLoad: ({ context, location, preload }) => {
    if (context.auth.loading) return;
    if (!context.auth.user) {
      // Capture on a real navigation to a protected route (a logged-out user clicking through),
      // but not on a speculative hover-preload whose target the user may never open.
      if (!preload) rememberDeniedUrl(context.auth.requiresJoin, location.href);
      throw redirect({ to: context.auth.requiresJoin ? "/join" : "/" });
    }
  },
  component: AuthedLayout,
});
